import React, { useState, useEffect } from 'react';
import { Check, ArrowRight, Loader2, Clock } from 'lucide-react';
import subscriptionService from '../../../services/subscriptionService';
import Button from '../../../components/ui/Button';

const PricingComparison = ({ currentPlan, onUpgrade }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [trialStatus, setTrialStatus] = useState({ inTrial: false, daysRemaining: 0 });

  useEffect(() => {
    loadPlans();
    loadTrialStatus();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    const { data } = await subscriptionService?.getAvailablePlans();
    setPlans(data || []);
    setLoading(false);
  };

  const loadTrialStatus = async () => {
    const trialInfo = await subscriptionService?.isUserInTrial();
    setTrialStatus(trialInfo);
  };

  const calculateSavings = (monthly, yearly) => {
    const monthlyCost = monthly * 12;
    const savings = ((monthlyCost - yearly) / monthlyCost) * 100;
    return Math.round(savings);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4">
        <span className={`text-sm font-medium ${billingInterval === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>
          Monthly
        </span>
        <button
          onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly')}
          className="relative w-14 h-7 bg-muted rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          <div className={`absolute top-1 left-1 w-5 h-5 bg-primary rounded-full transition-transform ${
            billingInterval === 'yearly' ? 'translate-x-7' : ''
          }`} />
        </button>
        <span className={`text-sm font-medium ${billingInterval === 'yearly' ? 'text-foreground' : 'text-muted-foreground'}`}>
          Yearly
          <span className="ml-2 text-xs text-success font-semibold">Save up to 17%</span>
        </span>
      </div>
      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
        {plans?.map((plan) => {
          const isCurrentPlan = plan?.planType === currentPlan;
          const isPremium = plan?.planType === 'premium';
          const isBasicUltra = plan?.planType === 'basic_ultra';
          const isBasic = plan?.planType === 'basic';
          const isFamilyPlan = plan?.planType === 'family';
          const price = billingInterval === 'yearly' ? plan?.priceYearly : plan?.priceMonthly;
          const displayPrice = billingInterval === 'yearly' ? (plan?.priceYearly / 12)?.toFixed(2) : plan?.priceMonthly;
          const savings = calculateSavings(plan?.priceMonthly, plan?.priceYearly);

          return (
            <div
              key={plan?.id}
              className={`
                bg-card rounded-xl border-2 p-6 transition-all
                ${isBasicUltra ? 'border-blue-500 shadow-lg' : ''}
                ${isPremium ? 'border-primary shadow-therapeutic-lg scale-105' : ''}
                ${isFamilyPlan ? 'border-purple-500 shadow-lg' : ''}
                ${isBasic && !isBasicUltra ? 'border-border' : ''}
                ${isCurrentPlan ? 'ring-2 ring-success' : ''}
              `}
            >
              {isBasic && trialStatus?.inTrial && (
                <div className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1 mb-4">
                  <Clock className="w-3 h-3" />
                  FREE TRIAL - {trialStatus?.daysRemaining} DAYS LEFT
                </div>
              )}
              {isBasicUltra && (
                <div className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                  BEST VALUE
                </div>
              )}
              {isPremium && (
                <div className="bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                  MOST POPULAR
                </div>
              )}
              {isCurrentPlan && (
                <div className="bg-success text-success-foreground text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                  CURRENT PLAN
                </div>
              )}

              <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
                {plan?.name}
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                {plan?.description}
              </p>

              <div className="mb-6">
                {plan?.planType === 'basic' ? (
                  <div>
                    <div className="text-4xl font-bold text-foreground">Free</div>
                    <p className="text-xs text-blue-600 mt-1 font-medium">1-month trial included</p>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-foreground">£{displayPrice}</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                    {billingInterval === 'yearly' && (
                      <p className="text-xs text-success mt-1">
                        Billed £{plan?.priceYearly}/year • Save {savings}%
                      </p>
                    )}
                  </div>
                )}
              </div>

              <Button
                onClick={() => !isCurrentPlan && onUpgrade()}
                disabled={isCurrentPlan}
                variant={isBasicUltra || isPremium ? 'default' : 'outline'}
                className={`w-full mb-6 ${
                  isFamilyPlan && !isCurrentPlan
                    ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300'
                    : ''
                }`}
              >
                {isCurrentPlan ? 'Current Plan' : isBasic ? 'Start Free Trial' : 'Upgrade Now'}
                {!isCurrentPlan && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>

              <div className="space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase">Features:</p>
                {plan?.features && Object.entries(plan?.features)?.map(([key, value]) => (
                  value && (
                    <div key={key} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground capitalize">
                        {key?.replace(/_/g, ' ')}
                      </span>
                    </div>
                  )
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-border space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase">Limits:</p>
                <div className="text-sm text-foreground space-y-1">
                  <div>History: {plan?.limits?.symptom_history_days === -1 ? 'Unlimited' : `${plan?.limits?.symptom_history_days} days`}</div>
                  <div>Exports: {plan?.limits?.monthly_exports === -1 ? 'Unlimited' : `${plan?.limits?.monthly_exports}/month`}</div>
                  <div>Caregivers: {plan?.limits?.caregiver_connections === -1 ? 'Unlimited' : plan?.limits?.caregiver_connections}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-center text-sm text-muted-foreground">
        <p>All plans include secure data storage and regular backups</p>
        <p className="mt-1">Cancel anytime • No hidden fees • 30-day money-back guarantee</p>
      </div>
    </div>
  );
};

export default PricingComparison;